import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { sd } from '@sd/lib/sdPaths';
import {
  Plus,
  Package,
  ExternalLink,
  Ticket as TicketIcon,
  ArrowUp,
  AlertTriangle,
  Minus,
  Search,
  Paperclip,
  Filter,
} from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import type { Ticket } from "@sd/types/ticket";

interface CommandPaletteProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onCreateTicket?: () => void;
  onSelectTicket?: (ticketId: string) => void;
}

export function CommandPalette({ open: controlledOpen, onOpenChange, onCreateTicket, onSelectTicket }: CommandPaletteProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  // Support both controlled and uncontrolled modes
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(!isOpen);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [isOpen, setOpen]);

  const handleSelect = useCallback((callback: () => void) => {
    setOpen(false);
    setSearch('');
    callback();
  }, []);

  /** Live ticket search not wired; palette keeps quick actions only. */
  const filteredTickets: Ticket[] = [];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'open':
        return <Badge className="bg-primary text-primary-foreground text-[10px] px-2 py-0.5 font-medium">Open</Badge>;
      case 'closed':
        return <Badge className="bg-muted text-muted-foreground text-[10px] px-2 py-0.5 font-medium">Closed</Badge>;
      case 'pending':
        return <Badge className="bg-warning text-warning-foreground text-[10px] px-2 py-0.5 font-medium">Pending</Badge>;
      case 'resolved':
        return <Badge className="bg-success text-success-foreground text-[10px] px-2 py-0.5 font-medium">Resolved</Badge>;
      default:
        return <Badge className="text-[10px] px-2 py-0.5">{status}</Badge>;
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'P1':
        return <AlertTriangle className="h-3.5 w-3.5 text-destructive" />;
      case 'P2':
        return <ArrowUp className="h-3.5 w-3.5 text-warning" />;
      case 'P3':
        return <Minus className="h-3.5 w-3.5 text-muted-foreground" />;
      default:
        return <Minus className="h-3.5 w-3.5 text-muted-foreground" />;
    }
  };

  return (
    <CommandDialog open={isOpen} onOpenChange={setOpen}>
      <CommandInput 
        placeholder="Search tickets, assets, people, attachments..." 
        value={search}
        onValueChange={setSearch}
      />
      <CommandList className="max-h-[450px]">
        <CommandEmpty>
          <div className="flex flex-col items-center gap-2 py-6">
            <Search className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No results found</p>
            <p className="text-xs text-muted-foreground">
              Try searching by ticket ID, title, description, or user name
            </p>
          </div>
        </CommandEmpty>
        
        <CommandGroup heading="Quick Actions">
          <CommandItem
            onSelect={() => handleSelect(() => onCreateTicket?.())}
            className="gap-3 py-3 cursor-pointer"
          >
            <Plus className="h-4 w-4 text-primary" />
            <span className="flex-1">Create New Ticket</span>
            <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border border-border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
              N
            </kbd>
          </CommandItem>
          <CommandItem
            onSelect={() => handleSelect(() => navigate(sd('assets')))}
            className="gap-3 py-3 cursor-pointer"
          >
            <Package className="h-4 w-4 text-muted-foreground" />
            <span>View All Assets</span>
          </CommandItem>
          <CommandItem
            onSelect={() => handleSelect(() => navigate(sd('reports')))}
            className="gap-3 py-3 cursor-pointer"
          >
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span>View Reports & Analytics</span>
          </CommandItem>
          <CommandItem
            onSelect={() => handleSelect(() => window.open('/portal', '_blank'))}
            className="gap-3 py-3 cursor-pointer"
          >
            <ExternalLink className="h-4 w-4 text-muted-foreground" />
            <span>Employee Portal</span>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading={`Tickets${search ? ` matching "${search}"` : ''}`}>
          {filteredTickets.map((ticket) => (
            <CommandItem
              key={ticket.id}
              onSelect={() => handleSelect(() => onSelectTicket?.(ticket.id))}
              className="py-3 cursor-pointer"
            >
              <div className="flex items-center gap-3 w-full">
                <TicketIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="text-xs font-mono text-muted-foreground flex-shrink-0">{ticket.id}</span>
                  {getPriorityIcon(ticket.priority)}
                  {getStatusBadge(ticket.status)}
                  {ticket.attachments && ticket.attachments.length > 0 && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Paperclip className="h-3 w-3" />
                      {ticket.attachments.length}
                    </span>
                  )}
                </div>
                <span className="text-xs text-muted-foreground flex-shrink-0">{ticket.user.name}</span>
              </div>
              <div className="pl-7 pt-1 w-full">
                <span className="text-sm text-foreground">{ticket.title}</span>
                {ticket.category && (
                  <span className="text-xs text-muted-foreground ml-2">• {ticket.category}</span>
                )}
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}

export function useCommandPalette() {
  const [open, setOpen] = useState(false);

  const openPalette = useCallback(() => setOpen(true), []);
  const closePalette = useCallback(() => setOpen(false), []);

  return { open, setOpen, openPalette, closePalette };
}
